import './AddressAutocomplete.scss';

import React, { useEffect, useId, useRef, useState } from 'react';

import {
  type AutocompleteSuggestionItem,
  parseAddressComponents,
  type ParsedAddress,
  useAutocompleteSuggestions,
} from '@hooks/useGoogleMapsAutocomplete';
import poweredByGoogleOnWhite from '@statics/images/powered_by_google_on_white.png';

export interface AddressAutocompleteProps {
  id: string;
  name?: string;
  value: string;
  placeholder?: string;
  autoComplete?: string;
  ariaInvalid?: boolean;
  onChange: (value: string) => void;
  onPlaceSelected: (address: ParsedAddress) => void;
  onBlur?: () => void;
}

// `onChange` = per-keystroke updates to the street field only.
// `onPlaceSelected` = one-shot fire on commit, carries parsed city/state/zip
// so the caller can fill the rest of the address form.
export default function AddressAutocomplete({
  id,
  name,
  value,
  placeholder,
  autoComplete,
  ariaInvalid,
  onChange,
  onPlaceSelected,
  onBlur,
}: AddressAutocompleteProps) {
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  // Set true just before we emit `onChange` from a suggestion selection; used
  // to suppress the next suggestion fetch that would otherwise re-open the
  // dropdown with the selected address as a new query. Reset on any further
  // user typing.
  const [suppressed, setSuppressed] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Only query autocomplete for the query the user is actively typing. When
  // suppressed (right after a selection), feed the hook an empty string so it
  // short-circuits instead of fetching the freshly-filled street value.
  const query = suppressed ? '' : value;
  const { suggestions, resetSession } = useAutocompleteSuggestions(query);

  const isOpen = isFocused && suggestions.length > 0;

  useEffect(() => {
    // Reset active highlight whenever the suggestion list changes.
    setActiveIndex(-1);
  }, [suggestions]);

  const commitSelection = async (item: AutocompleteSuggestionItem) => {
    try {
      const place = item.placePrediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents'] });
      const parsed = parseAddressComponents(place.addressComponents ?? []);

      setSuppressed(true);
      onChange(parsed.street);
      onPlaceSelected(parsed);
      resetSession();
      setActiveIndex(-1);
      inputRef.current?.blur();
    } catch (err) {
      console.warn('[AddressAutocomplete] failed to fetch place fields', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (suppressed) setSuppressed(false);
    onChange(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;
    const count = suggestions.length;
    if (!count) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % count);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? count - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0) {
        e.preventDefault();
        commitSelection(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setIsFocused(false);
      setActiveIndex(-1);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  // Clicking a listbox option doesn't blur the input because options
  // `preventDefault()` on `mousedown` (keeps focus on the input long enough
  // for `click` to commit). So a real blur here means focus has actually
  // left — close immediately, no grace period needed.
  const handleBlur = () => {
    setIsFocused(false);
    setActiveIndex(-1);
    onBlur?.();
  };

  const activeOptionId = activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined;

  return (
    <div className='po-autocomplete'>
      <input
        ref={inputRef}
        id={id}
        name={name ?? id}
        className='po-input'
        type='text'
        placeholder={placeholder}
        autoComplete={autoComplete ?? 'off'}
        value={value}
        role='combobox'
        aria-autocomplete='list'
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={activeOptionId}
        aria-invalid={!!ariaInvalid}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {isOpen ? (
        <ul id={listboxId} role='listbox' className='po-autocomplete-listbox'>
          {suggestions.map((item, i) => {
            const optionId = `${listboxId}-opt-${i}`;
            const isActive = i === activeIndex;
            return (
              <li
                key={optionId}
                id={optionId}
                role='option'
                aria-selected={isActive}
                className={`po-autocomplete-listbox__option${isActive ? ' po-autocomplete-listbox__option--active' : ''}`}
                onMouseDown={e => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => commitSelection(item)}
              >
                {item.text}
              </li>
            );
          })}
          <li className='po-autocomplete-listbox__attribution' role='presentation'>
            <img src={poweredByGoogleOnWhite} alt='Powered by Google' />
          </li>
        </ul>
      ) : null}
    </div>
  );
}
